/**
 * voice-v8 — Wiederverwertbare Vorlese-Engine (Narration).
 *
 * EIN gemeinsamer Audio-Kern für alle Vorlese-Stellen (Essenz-Spiegel,
 * Human Design, „Mein Leben"). Die Engine besitzt GENAU EIN `<audio>`-Element
 * und einen expliziten Zustandsautomaten. Sie weiß NICHT, wie die MP3-URL
 * zustande kommt (Streaming-Token vs. fertige Datei) — das kapselt der Aufrufer
 * über eine {@link AudioSource}. So bekommen ALLE Pfade dieselbe Robustheit
 * (Timeouts, Stall-Watchdog, Abbruch laufender Anfragen) und dasselbe
 * UI-Verhalten umsonst.
 *
 * Designziele (aus dem Audit):
 *  - Kein Einfrieren, keine ewige Lade-Animation, kein „Pause-Icon für immer"
 *    auf einem toten/stockenden Stream.
 *  - Fehlerzustand ist IMMER erholbar: der nächste Klick versucht es erneut.
 *  - `busy` ist ABGELEITET aus dem Zustand, niemals ein eigenes Flag.
 */

import { hookHiFiPlayback, prewarmHiFi } from "./playback-hifi.js";
import { meldeVorleseVerlust, neuerVorlesevorgang } from "./vorlese-verlust.js";
import { betritt, type Buehnenplatz } from "./ton-leitstand.js";

/**
 * Was ein Vorlauf zurueckbringt.
 *
 * `url: ""` ohne `fehler` heisst „hier gibt es nichts zu sprechen" (ein
 * Stueck aus lauter Auszeichnung). Mit `fehler` heisst es „es ist etwas
 * schiefgegangen". Die beiden zu verwechseln kostete einen ganzen Absatz.
 */
type StueckErgebnis = { url: string; fehler?: unknown; warFehler?: boolean };

/** Liefert die abspielbare MP3-URL — abstrahiert Streaming- vs. Datei-Pfad. */
export interface AudioSource {
  /**
   * Beschafft die MP3-URL. `signal` wird abgebrochen, sobald eine neue Anfrage
   * startet oder die Engine zerstört wird → die Implementierung MUSS ihre
   * `fetch`-Aufrufe damit verdrahten und bei `AbortError` durchwerfen.
   */
  resolveUrl(signal: AbortSignal): Promise<string>;
  /**
   * Wie viele Zeichen vertont werden — bestimmt, wie lange gewartet wird.
   * Fehlt die Angabe, gilt das Grundbudget.
   */
  zeichen?: number;
  /**
   * Was hier vorgelesen wird — für die Abspielleiste. Fehlt es, steht dort
   * schlicht „Vorlesen".
   */
  titel?: string;
  /**
   * Der Text in sprechbaren Stücken — der Weg gegen die lange Stille.
   *
   * ## Der Befund (Ticket 2047)
   *
   * `/api/tts/stream` heißt „stream", streamt aber nicht: Der Server
   * synthetisiert die ganze Antwort und schickt sie in einem Stück.
   * Gemessen: **11.800 ms Stille bei 272 Zeichen** — der einzige
   * zweistellige Sekundenwert im ganzen Vorlese-Pfad.
   *
   * Zweimal wurde versucht, den Server streamen zu lassen. Beide Male
   * scheiterte es an Safari: ohne bekannte Gesamtlänge bricht es nach dem
   * ersten Stück ab („Media failed to decode"), und ohne Bereichs-Anfragen
   * lässt sich nicht springen. Der Code dafür steht noch da, abgeschaltet.
   *
   * ## Der dritte Weg
   *
   * Nicht der Server streamt — der Client fragt öfter. Jede Anfrage bleibt
   * eine vollständige, kurze Datei mit Länge und Bereichs-Unterstützung
   * (Safari zufrieden), aber der erste Ton kommt nach dem ERSTEN Stück
   * statt nach der ganzen Antwort.
   *
   * Genau so arbeitet die Live-Sprache seit jeher (`tts-queue.ts`) — dort
   * war die Stille nie ein Thema. Der Vorlese-Weg hat dieses Muster nur nie
   * übernommen.
   *
   * Fehlt das Feld, bleibt alles beim Alten: eine URL, ein Stück.
   */
  stuecke?: string[];
  /** Beschafft die URL für EIN Stück. Nötig, sobald `stuecke` gesetzt ist. */
  resolveStueck?(text: string, signal: AbortSignal): Promise<string>;
  /**
   * Liegt der GANZE Text schon fertig vertont da? (05.09.2026)
   *
   * Dann gibt es keinen Grund für Stücke: Eine Datei mit bekannter Länge
   * ist springbar, ihre Dauer ist exakt, und die Leiste zeigt, was läuft.
   * Stücke sind der Weg für den KALTEN Start — nicht der Normalfall.
   *
   * Martin, mit Foto: *„Der Player passt überhaupt nicht mit dem zusammen,
   * was im Hintergrund läuft. Manchmal stehen nur sieben Sekunden da, aber
   * läuft trotzdem weiter."* — Das waren Stücke: Bei jedem Stückwechsel
   * sprang die Anzeige auf 0:00, die Gesamtdauer kannte niemand.
   *
   * Gibt die URL zurück, wenn es sie gibt, sonst null. Wirft nie.
   */
  pruefeGanz?(signal: AbortSignal): Promise<string | null>;
  /**
   * Wo das Vorlesen beginnt (Sekunden im Ganzen) — der gemerkte Stand.
   * (10.09.2026) Die Engine wendet ihn EINMAL an: in der Datei ueber
   * `loadedmetadata`, in den Stuecken ueber das passende Stueck. Vorher
   * setzte der Aufrufer die Sekunde an <audio> — bei JEDEM geladenen Stueck.
   */
  startSek?: number;
}

/** Expliziter Zustand des Players. `busy` ist daraus abgeleitet. */
export type NarrationState =
  | "idle"
  | "preparing"
  | "buffering"
  | "playing"
  | "paused"
  | "stalled"
  | "error";

/** True, solange die Engine arbeitet (URL beschaffen / Erst-Puffern). Nur
 * intern genutzt (isBusy()-Methode) — kein Export nötig. */
/** Gemessen am 04.09. mit Soniox: ~15,5 Zeichen je Sekunde Sprache. */
const ZEICHEN_JE_SEKUNDE = 15.5;

/**
 * Der VORSPRUNG — warum das Vorlesen einen braucht. (07.09.2026, Ticket 2140)
 *
 * Martin: *„Das Vorlesen war eine Katastrophe. Mindestens 30 Prozent vom
 * Audio fehlen. Wortfetzen dazwischen sind unterbrochen, dann lange Pausen."*
 * Vesperas Ticket nennt die Ursache richtig: **Erzeugung ist langsamer als
 * die Wiedergabe.**
 *
 * ## Die Rechnung
 *
 * | | |
 * |---|---|
 * | Soniox v2 erzeugt | ~56 ms je Zeichen (gemessen: 273 Zeichen → 15,4 s) |
 * | Deutsche Sprache dauert | ~64 ms je Zeichen (15,5 Zeichen/s) |
 * | ein einzelner Strom | 0,87× Echtzeit — knapp schneller |
 * | in Martins echter Vorlesung | **79 bis 180 ms je Zeichen** |
 *
 * Der Unterschied: Im Protokoll standen 13 Soniox-Aufrufe in 50 Sekunden.
 * Soniox hat zwei Plaetze (gemessen 27.07.: einer 13,2 s, zwei 14,1 s, vier
 * 27,2 s — mehr Parallelitaet macht es LANGSAMER). Die Stuecke bremsen sich
 * gegenseitig aus, und ohne Vorsprung faellt die Wiedergabe mit jedem Stueck
 * weiter zurueck.
 *
 * ## Die Regel
 *
 * Erst sammeln, dann sprechen. Der erste Ton kommt spaeter — dafuer kommt er
 * durch. Ein hoerbares Warten am Anfang ist ehrlich; ein Loch mitten im Satz
 * ist ein Fehler.
 *
 * ⚠️ Der Vorsprung waechst mit dem Text, aber nicht unbegrenzt: Bei einem
 *    langen Spiegel waeren 25 % sonst anderthalb Minuten Vorlauf.
 */
const VORLAUF_MIN_SEK = 10;
const VORLAUF_ANTEIL = 0.25;
const VORLAUF_MAX_SEK = 45;
/**
 * So viele Beschaffungen gleichzeitig.
 *
 * ⚠️ ZWEI, nicht mehr. Das ist keine Vorsicht, sondern gemessen: vier
 *    parallele Anfragen brauchen zusammen 27,2 s fuer das, wofuer zwei 14,1 s
 *    brauchen. Wer hier aufdreht, macht es langsamer.
 */
const STUECK_PARALLEL = 2;

/** Ein Stueck auf dem Weg vom Text zum Ton. */
interface StueckPosten {
  text: string;
  /** Geschaetzte Dauer — bekannt, bevor der Ton da ist. */
  sek: number;
  p: Promise<StueckErgebnis>;
  fertig: boolean;
  erg: StueckErgebnis | null;
  /** Vom Vorrat verworfen — die Adresse wird bei Ankunft gleich freigegeben. */
  verworfen?: boolean;
}

/**
 * Welches Stück enthält die Position? Rein, testbar: Dauern in Sekunden,
 * Ziel in Sekunden → Index des Stücks und die Sekunden davor.
 */
export function stueckFuerPosition(
  dauern: readonly number[],
  ziel: number,
): { index: number; davor: number } {
  let davor = 0;
  for (let i = 0; i < dauern.length; i++) {
    const d = dauern[i] ?? 0;
    if (ziel < davor + d || i === dauern.length - 1) return { index: i, davor };
    davor += d;
  }
  return { index: 0, davor: 0 };
}

function isBusyState(state: NarrationState): boolean {
  return state === "preparing" || state === "buffering";
}
/*
 * ⚠️ 2026-09-04 (Martin, 06:02: *„Die Spur lädt ewig und es kommt nicht."*)
 *
 * Hier stand eine FESTE Frist von 120 s, mit dem Kommentar „lange
 * Essenz-Abschnitte brauchen oft 30–90 s". Das galt für eine andere Stimme.
 *
 * Martins Stimme läuft über Soniox, und das vertont ungefähr in ECHTZEIT.
 * Gemessen am 27.07.: rund 0,07 s je Zeichen plus 5 s Grundkosten — und am
 * 04.09. an einem echten Lauf nachgeprüft: 2000 Zeichen brauchten 133 s.
 *
 * Damit war jeder Text über etwa 1600 Zeichen für den Abspieler
 * unerreichbar. Er brach ab, bevor der Ton je fertig wurde, und zeigte
 * „Bereitet vor …" bis zum Aufgeben.
 *
 * ⚠️ Der SERVER hat genau diese Lehre schon am 27.07. gezogen (Ticket 795):
 * seine Frist richtet sich seither nach der Textlänge. Der Abspieler hat sie
 * nie mitbekommen — dasselbe Muster wie bei der Modellwahl gestern: eine
 * Seite lernt, die andere nicht.
 *
 * Die Formel hier ist bewusst großzügiger als die Messung (0,09 statt 0,07,
 * 20 s statt 5 s Grundlast): ein zu knappes Budget kostet den ganzen Ton, ein
 * zu weites nur Geduld im Fehlerfall.
 */
const SYNTH_GRUNDLAST_MS = 20_000;
const SYNTH_JE_ZEICHEN_MS = 90;
/** Untergrenze — kein Text wird dadurch schlechter behandelt als bisher. */
const SYNTH_TIMEOUT_MS = 120_000;
/** Obergrenze: irgendwann ist es kein langsamer Anbieter mehr, sondern tot. */
const SYNTH_TIMEOUT_MAX_MS = 420_000;

/** Wie lange auf die Vertonung gewartet wird. */
export function synthFrist(zeichen?: number): number {
  if (!zeichen || zeichen <= 0) return SYNTH_TIMEOUT_MS;
  const gerechnet = SYNTH_GRUNDLAST_MS + zeichen * SYNTH_JE_ZEICHEN_MS;
  return Math.min(SYNTH_TIMEOUT_MAX_MS, Math.max(SYNTH_TIMEOUT_MS, gerechnet));
}
// Stall-Watchdog: nach `stalled`/`waiting` so lange auf Fortschritt warten.
// 18s (war 10s): der erste Streaming-Chunk kann mit ElevenLabs-Quota-Fallback
// (erst ElevenLabs-Fehlversuch, dann Google-HD) ~12-15s bis zum ersten Ton
// brauchen — 10s feuerten zu früh „Erneut versuchen". Ein wirklich toter Stream
// fällt weiterhin (nach 18s) sauber in den erholbaren Fehlerzustand.
// GILT NUR fuer einen Aussetzer MITTEN in der Wiedergabe.
const STALL_TIMEOUT_MS = 18_000;
// 2026-07-25 (Martin, "Spiegel bleibt bei Track 6 haengen"): Der ANLAUF ist
// etwas anderes als ein Aussetzer. Beim Abschnittswechsel feuert das <audio>
// sofort "waiting", waehrend der Server die Vertonung ueberhaupt erst baut —
// Warten ist hier NORMAL, kein Fehler. Gemessen am Live-Server: 266 Zeichen →
// 15,5s bis zum ersten Ton, 2669 Zeichen → 30,5s. Mit den alten 18s brach der
// Player lange Abschnitte also zwingend ab (nginx 499). 45s = gemessenes
// Maximum + Marge; ein wirklich toter Anlauf faellt weiterhin sauber in den
// erholbaren Fehlerzustand, nur eben spaeter.
const FIRST_CHUNK_TIMEOUT_MS = 45_000;

/**
 * Bricht `promise` ab, wenn `ms` verstreichen, ohne dass es auflöst — und
 * abortet dabei den geteilten Controller (damit auch das darunterliegende
 * `fetch` endet). Bei Timeout wirft die zurückgegebene Promise einen Fehler
 * mit `name === "TimeoutError"`.
 */
function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  controller: AbortController,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      controller.abort();
      const err = new Error(`narration timed out after ${ms}ms`);
      err.name = "TimeoutError";
      reject(err);
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err instanceof Error ? err : new Error(String(err)));
      },
    );
  });
}

/**
 * 2026-07-24 (Martin, Spiegel bricht nach dem ersten Abschnitt ab): meldet den
 * ECHTEN Grund eines Wiedergabe-Abbruchs an den bestehenden Diagnose-Kanal.
 * Ohne das sieht der Server nur abgebrochene Streams (nginx 499) und kann
 * nicht unterscheiden zwischen "Browser verweigert Autoplay"
 * (NotAllowedError), "Quelle kaputt" (MEDIA_ERR_*) und "Zeitueberschreitung".
 * Fire-and-forget — die Wiedergabe haengt NIE daran.
 */
function reportPlaybackIssue(detail: Record<string, unknown>): void {
  try {
    void fetch("/api/debug/audio-state", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      keepalive: true,
      body: JSON.stringify({ when: "narration-issue", ...detail }),
    }).catch(() => undefined);
  } catch {
    /* Diagnose ist Kuer */
  }
}

function isAbortError(err: unknown): boolean {
  return (err as { name?: string } | null)?.name === "AbortError";
}

/**
 * EINE Vorlese-Engine über EINEM `<audio>`-Element. Die UI verdrahtet sich
 * ausschließlich über die Callbacks {@link NarrationEngine.onState},
 * {@link NarrationEngine.onTime} und {@link NarrationEngine.onEnded}.
 */
// #3 (2026-06-28): Nur EIN aktiver Player global. Startet eine Engine die
// Wiedergabe, wird die vorher aktive pausiert (Position bleibt erhalten).
let activeNarrationEngine: NarrationEngine | null = null;

export class NarrationEngine {
  /** Eigenes Audio-Element — nichts außerhalb der Engine fasst es an. */
  readonly audio: HTMLAudioElement;

  /** Wird bei jedem Zustandswechsel aufgerufen (auch bei Wiederholung). */
  onState: (state: NarrationState) => void = () => {};
  /** Fortschritt beim Abspielen — Sekunden (aktuell, gesamt|NaN). */
  /**
   * Fortschritt — über Stückgrenzen hinweg, in EINER Rechnung.
   *
   * `geschaetzt` heißt: Die Dauer ist aus der Zeichenzahl gerechnet, nicht
   * gemessen (Stücke im kalten Start). Die Leiste zeigt sie dann mit Tilde.
   */
  onTime: (current: number, duration: number, geschaetzt: boolean) => void =
    () => {};
  /** Stück fertig abgespielt. */
  onEnded: () => void = () => {};

  private state: NarrationState = "idle";
  /** Controller der aktuell laufenden `resolveUrl`-Anfrage. */
  private reqAbort: AbortController | null = null;
  /** Stall-Watchdog-Timer (läuft nach `stalled`/`waiting`). */
  private stallTimer: ReturnType<typeof setTimeout> | null = null;
  /** Verhindert Auflösen einer veralteten Anfrage nach einem Neustart. */
  private loadSeq = 0;
  /**
   * Welcher Lauf sein Ende schon gemeldet hat.
   *
   * ⚠️⚠️ 15.09.2026 — Martin: *„Und dann springt er ploetzlich auf Track 4,
   *    obwohl ich gerade beim zweiten war."*
   *
   * `onEnded` schaltet beim Aufrufer GENAU EINEN Abschnitt weiter. Zwei
   * Spruenge heissen also: Das Ende wurde zweimal gemeldet. Ein `ended` kann
   * mehrfach kommen — ein Stueck, dessen Vertonung scheitert, ist leer und
   * endet sofort; dasselbe Element feuert dann erneut, waehrend der naechste
   * Abschnitt schon laedt.
   *
   * Ein Lauf meldet sein Ende hoechstens einmal.
   */
  private endeGemeldetSeq = -1;
  /** Platz auf der Bühne des Ton-Leitstands, solange vorgelesen wird. */
  private platz: Buehnenplatz | null = null;
  /** Titel der zuletzt geladenen Quelle — `togglePlayPause` kennt keine. */
  private letzterTitel = "";
  /*
   * Stückweises Vorlesen (Ticket 2047). Leer = der bisherige Weg mit einer
   * einzigen URL, unverändert.
   */
  /** Die noch nicht gespielten Stücke, in Reihenfolge. */
  private restStuecke: string[] = [];
  /** Die Quelle, die sie beschafft — solange diese Wiedergabe läuft. */
  private stueckQuelle: AudioSource | null = null;
  /** Schon geholte URL des NÄCHSTEN Stücks (wird während des Spielens geladen). */
  /**
   * Der Vorrat: Stuecke, die schon angefordert sind — fertig oder unterwegs.
   *
   * ⚠️ Die Reihenfolge ist die des Textes. Die Anfragen laufen parallel und
   *    kommen unsortiert zurueck; weil hier das VERSPRECHEN in der richtigen
   *    Reihenfolge liegt, kann nichts vertauscht werden.
   */
  private vorrat: StueckPosten[] = [];
  /** Wie viele Beschaffungen gerade laufen (hoechstens STUECK_PARALLEL). */
  private offeneAnfragen = 0;
  /** Sekunden der bereits fertig gespielten Stücke — für die Abspielleiste. */
  private gespielteSek = 0;
  /** Ziel innerhalb des nächsten Stücks (Sekunden) — gesetzt von springeZu. */
  private zielOffset = 0;
  /** Zeichen der geladenen Quelle — auch wenn sie als EINE Datei spielt. */
  private quelleZeichen = 0;
  /** Die letzte Dauer, die dem Kopf spuerbar voraus war — je Lauf. */
  private gemesseneDauer = 0;
  /** Die zuletzt vom Element gemeldete Dauer — um Wachstum zu erkennen. */
  private zuletztGemessen = 0;
  /** Die Dauer ist im Lauf schon einmal gewachsen — die Quelle ist ein Strom. */
  private istStrom = false;
  /*
   * ⚠️⚠️ 15.09.2026 — DIE UHR STAND, DER TON LIEF. (Martin, 12:29, Foto)
   *
   * Spiegel 3/4, Knopf auf Pause (also spielt er), Uhr auf 0:01, seit
   * Minuten. Im Protokoll: der Abschnitt kam als fertige Datei (206, 1,1 MB)
   * — kein Strom, nichts Exotisches. Die Anzeige haengt an `timeupdate`;
   * bleibt das aus oder friert `currentTime` ein, waehrend das Element
   * spielt, steht die Uhr, und niemand erfaehrt es.
   *
   * Deshalb zwei Dinge:
   *   1. Ein eigener Takt (alle 500 ms), solange gespielt wird — die Anzeige
   *      haengt nicht mehr an einem Ereignis, das kommen kann oder nicht.
   *   2. Ein Waechter: Steht `currentTime` zwei Sekunden lang still, obwohl
   *      das Element weder pausiert noch zu Ende ist, wird das EINMAL ans
   *      Journal gemeldet (Stufe „uhr-steht", mit allem, was das Element
   *      weiss) — und die Uhr laeuft nach der Wanduhr weiter, bis das
   *      Element wieder mitkommt. Der naechste Vorfall sagt dann selbst,
   *      was los war, statt dass hier zum zwanzigsten Mal geraten wird.
   */
  private takt: ReturnType<typeof setInterval> | null = null;
  private zuletztUhr = { wann: 0, zeit: -1 };
  private uhrGefrorenSeit = 0;
  private uhrGemeldet = false;
  /** Die blob:-Adresse des laufenden Stücks — beim nächsten wieder frei. */
  private laufendeStueckUrl = "";

  constructor(audio?: HTMLAudioElement) {
    this.audio = audio ?? document.createElement("audio");
    this.audio.preload = "none";
    // 2026-07-15 (Martin): Vorlesen/TTS ueber Bluetooth ebenfalls in
    // hochwertiger A2DP-Qualitaet (nicht Telefon-/HFP-Profil). Die Engine
    // ist der gemeinsame Kern ALLER Vorlese-Stellen -> eine Zeile genuegt.
    hookHiFiPlayback(this.audio);

    this.audio.addEventListener("loadedmetadata", () => {
      // Ein Sprung in ein anderes Stück: erst wenn das Stück da ist, an die
      // Stelle darin gehen.
      if (this.zielOffset > 0) {
        const d = Number.isFinite(this.audio.duration) ? this.audio.duration : 0;
        try {
          this.audio.currentTime = d > 0 ? Math.min(this.zielOffset, Math.max(0, d - 0.25)) : this.zielOffset;
        } catch {
          /* manche Browser erst nach canplay — dann bleibt der Anfang */
        }
        this.zielOffset = 0;
      }
    });
    this.audio.addEventListener("playing", () => {
      this.clearStallTimer();
      this.setState("playing");
      this.starteTakt();
    });
    // `waiting`/`stalled`: Browser wartet auf Daten → puffern + Watchdog.
    const onWaiting = (): void => {
      // Beim Erst-Puffern bleiben wir in „buffering"; läuft die Wiedergabe
      // schon, zeigen wir „stalled". Watchdog läuft in beiden Fällen.
      if (this.state !== "buffering") this.setState("stalled");
      this.armStallTimer();
    };
    this.audio.addEventListener("waiting", onWaiting);
    this.audio.addEventListener("stalled", onWaiting);
    this.audio.addEventListener("error", () => {
      const e = this.audio.error;
      reportPlaybackIssue({
        stage: "element",
        code: e ? e.code : null,
        message: e ? e.message : "",
        src: this.audio.currentSrc ? this.audio.currentSrc.slice(-60) : "",
      });
      this.fail();
    });
    this.audio.addEventListener("pause", () => {
      // `pause` feuert auch am Ende — `ended` hat dort schon den Zustand
      // gesetzt, also nicht überschreiben.
      if (this.audio.ended) return;
      if (
        this.state === "playing" ||
        this.state === "stalled" ||
        this.state === "buffering"
      ) {
        this.clearStallTimer();
        this.setState("paused");
      }
      this.stoppeTakt();
    });
    this.audio.addEventListener("ended", () => {
      this.clearStallTimer();
      this.stoppeTakt();
      // Im Fehlerzustand (Stall-Watchdog hat ausgelöst) kein Auto-Advance: das
      // Audio hat nur den Rest-Buffer gespielt, obwohl der Server versagt hat.
      // onEnded() hier zu rufen würde zum nächsten Abschnitt springen (#87).
      const endedClean = this.state !== "error";
      // Ticket 2047: Ein Stück ist zu Ende — der TEXT aber noch nicht. Erst
      // wenn nichts mehr wartet, ist das Vorlesen fertig; sonst hörte der
      // Abschnitt nach dem ersten Satz auf, und der Aufrufer sprünge weiter.
      // ⚠️ Der VORRAT zaehlt mit. Ohne ihn galt das Vorlesen als fertig,
      //    sobald nichts mehr angefordert werden musste — obwohl noch
      //    Stuecke abspielbereit lagen.
      if (endedClean && (this.restStuecke.length > 0 || this.vorrat.length > 0)) {
        /*
         * ⚠️⚠️ 15.09.2026 — HIER STAND `: 0`, UND DAMIT BLIEB DIE ZEIT STEHEN.
         *
         * Martin, mit Foto: *„Die Zeitanzeige zeigt in sehr vielen Momenten
         * eine Sekunde an. Was ist das für ein Scheiß, obwohl der Player
         * schon seit Minuten läuft?"*
         *
         * Ein gestreamtes Stueck (Cache-Miss, keine Content-Length) hat
         * `duration = NaN`. Der alte Ausdruck zaehlte dann NULL Sekunden auf
         * das Konto — jedes Stueck begann die Zeitrechnung von vorn. Im Test
         * nachgestellt: Stueck 1 laeuft bis Sekunde 20, Stueck 2 beginnt, und
         * die Leiste zeigt 3 statt 23.
         *
         * `currentTime` beim `ended` weiss es trotzdem: Es ist die Stelle, an
         * der der Ton aufgehoert hat — auch ohne bekannte Gesamtlaenge. Die
         * gemessene Dauer bleibt der erste Weg, weil sie genauer ist; der
         * Abspielkopf ist der Rueckfall, nicht der Ersatz.
         *
         * ⚠️ Daran haengt mehr als die Anzeige: `springeZu` rechnet ein
         *    Sprungziel gegen `gespielteSek` um. Ein Konto, das nicht
         *    mitwaechst, macht auch den Regler unbrauchbar.
         */
        const gemessen = Number.isFinite(this.audio.duration)
          ? this.audio.duration
          : 0;
        const gespielt =
          gemessen > 0 ? gemessen : Math.max(0, this.audio.currentTime || 0);
        this.gespielteSek += gespielt;
        void this.spieleNaechstesStueck();
        return;
      }
      this.stueckQuelle = null;
      this.leereVorrat();
      this.gespielteSek = 0;
      this.setState("idle");
      const schonGemeldet = this.endeGemeldetSeq === this.loadSeq;
      if (endedClean && !schonGemeldet) {
        this.endeGemeldetSeq = this.loadSeq;
        this.onEnded();
      }
    });
    this.audio.addEventListener("timeupdate", () => {
      // Fortschritt = Stream lebt → Watchdog entschärfen, „playing" sichern.
      this.clearStallTimer();
      if (this.state === "stalled" || this.state === "buffering") {
        this.setState("playing");
      }
      const st = this.stand();
      this.onTime(st.position, st.dauer, st.geschaetzt);
    });
  }

  /**
   * Wo wir sind und wie lang das Ganze ist — EINE Rechnung. (05.09.2026)
   *
   * Vorher zwei: Der Leitstand bekam `gespielteSek + currentTime`, die
   * Leiste nur `currentTime`. Bei Stücken sprang die Leiste deshalb bei
   * jedem Wechsel auf null zurück, während der Leitstand richtig lag.
   * Martin sah beides und traute keinem mehr.
   *
   * Dauer: bei einer Datei die echte; bei Stücken aus der Zeichenzahl
   * geschätzt (Soniox spricht ~15,5 Zeichen je Sekunde, gemessen am 04.09.)
   * und als Schätzung gekennzeichnet — besser eine Tilde als ein Strich.
   */
  /**
   * An eine Stelle springen — auch bei geschätzter Dauer, auch über
   * Stückgrenzen. (06.09.2026, Martin: „den Regler nicht verschieben kann")
   *
   * Innerhalb des laufenden Stücks ist der Sprung exakt (currentTime). Liegt
   * das Ziel in einem anderen Stück, wird es aus den Zeichenzahlen geschätzt,
   * das Stück geladen und der Rest darin angesteuert (loadedmetadata). Bei
   * einer einzelnen Datei springt das Element selbst.
   */
  springeZu(ziel: number): void {
    const z = Math.max(0, ziel);
    const quelle = this.stueckQuelle;
    const alle = quelle?.stuecke ?? [];
    if (!quelle || alle.length === 0) {
      try {
        this.audio.currentTime = z;
      } catch {
        /* egal */
      }
      return;
    }
    const laufendeDauer = Number.isFinite(this.audio.duration) && this.audio.duration > 0
      ? this.audio.duration
      : (alle[
          alle.length - this.restStuecke.length - this.vorrat.length - 1
        ]?.length ?? 0) / ZEICHEN_JE_SEKUNDE;
    if (z >= this.gespielteSek && z < this.gespielteSek + laufendeDauer) {
      try {
        this.audio.currentTime = z - this.gespielteSek;
      } catch {
        /* egal */
      }
      return;
    }
    const dauern = alle.map((t) => t.length / ZEICHEN_JE_SEKUNDE);
    const { index, davor } = stueckFuerPosition(dauern, z);
    this.abortRequest();
    this.restStuecke = alle.slice(index);
    this.gespielteSek = davor;
    // Der alte Vorrat gehoert zu einer anderen Stelle im Text.
    this.leereVorrat();
    this.zielOffset = z - davor;
    void this.spieleNaechstesStueck();
  }

  private stand(): { position: number; dauer: number; geschaetzt: boolean } {
    // Steht das Element still, obwohl es spielt, zaehlt die Wanduhr weiter
    // (siehe Uhr-Waechter) — die Anzeige bleibt nie wieder bei 0:01 stehen.
    const wanduhr =
      this.uhrGefrorenSeit > 0 ? (Date.now() - this.uhrGefrorenSeit) / 1000 : 0;
    const position = this.gespielteSek + (this.audio.currentTime || 0) + wanduhr;
    const einzeln =
      this.restStuecke.length === 0 &&
      this.vorrat.length === 0 &&
      this.gespielteSek === 0;
    const zeichen =
      this.stueckQuelle?.zeichen ?? this.quelleZeichen ?? 0;
    /*
     * ⚠️⚠️ 15.09.2026 — DIE GEMESSENE DAUER WAR DER PUFFERRAND.
     *
     * Martin, mit Foto aus dem Datei-Betrachter: „0:12 / –", Abschnitt 7
     * von 25, seit Sekunden am Laufen. Die Leiste hatte KEINE Dauer.
     *
     * Der Weg dorthin: Der Abschnitt lag als EINE Datei vor (vorgewaermt),
     * also `einzeln`. Safari meldet bei einer gestreamten Datei eine
     * endliche `duration` — nur ist sie nicht das Ende, sondern die Kante
     * des bisher Geladenen, und die laeuft dem Abspielkopf um Haaresbreite
     * voraus. Diese Zahl ging hier als GEMESSEN hinaus; die Leiste haelt eine
     * Dauer, die dem Kopf nicht mindestens 0,75 s vorausliegt, zu Recht fuer
     * unglaubwuerdig und zeigt „–". Die Schaetzung aus der Zeichenzahl, die
     * die Engine laengst hatte, kam nie zum Zug — dieser Zweig stand davor.
     *
     * Jetzt gilt dieselbe Glaubwuerdigkeitsregel schon hier: Eine gemessene
     * Dauer zaehlt nur, wenn sie dem Kopf spuerbar voraus ist. Sonst kommt
     * die Schaetzung — mit Tilde, ehrlich — und der Regler lebt.
     */
    const gemessen = this.audio.duration;
    /*
     * Dritter Pruefer, 15.09.: Ein Rand, der dem Kopf dauerhaft mehr als
     * 0,75 s voraus bleibt (schneller Anbieter), ging weiter als GEMESSEN
     * hinaus — eine wachsende Zahl ohne Tilde. Eine Dauer, die im Lauf
     * einmal GEWACHSEN ist, gehoert zu einem Strom; ab da zaehlt bei
     * bekannter Zeichenzahl nur noch die Schaetzung.
     */
    if (
      Number.isFinite(gemessen) &&
      this.zuletztGemessen > 0 &&
      gemessen > this.zuletztGemessen + 0.05
    ) {
      this.istStrom = true;
      this.gemesseneDauer = 0;
    }
    const gemessenGlaubwuerdig =
      Number.isFinite(gemessen) &&
      gemessen > 0 &&
      gemessen - position > 0.75 &&
      !(this.istStrom && zeichen > 0);
    if (einzeln && gemessenGlaubwuerdig) {
      /*
       * Einmal glaubwuerdig, bleibt sie es — ABER nur, wenn die Zahl seit der
       * letzten Lesung nicht gewachsen ist. Eine Dauer, die zwischen zwei
       * Lesungen waechst, ist per Definition die Pufferkante, keine Dauer
       * (Pruefer-Befund 15.09.: der Rand liegt beim Start eines Streams fast
       * immer mehr als 0,75 s voraus und waere sonst eingefroren worden).
       * Stabil heisst: dieselbe Zahl wie zuletzt gesehen.
       */
      const stabil =
        this.zuletztGemessen > 0 && Math.abs(gemessen - this.zuletztGemessen) < 0.05;
      this.zuletztGemessen = gemessen;
      if (stabil) this.gemesseneDauer = gemessen;
      return { position, dauer: gemessen, geschaetzt: false };
    }
    if (Number.isFinite(gemessen)) this.zuletztGemessen = gemessen;
    if (einzeln && this.gemesseneDauer > position) {
      return { position, dauer: this.gemesseneDauer, geschaetzt: false };
    }
    // 10.09.: Auch die EINE Datei hat eine Zeichenzahl — solange <audio> die
    // Dauer noch nicht kennt (Safari liest sie erst vom Dateiende), zeigt
    // die Leiste die Schaetzung statt „–".
    if (zeichen > 0) {
      return { position, dauer: zeichen / ZEICHEN_JE_SEKUNDE, geschaetzt: true };
    }
    // Ohne Zeichenzahl bleibt nur die gemessene Zahl — die Leiste entscheidet
    // selbst, ob sie ihr glaubt.
    if (einzeln && Number.isFinite(gemessen) && gemessen > 0) {
      return { position, dauer: gemessen, geschaetzt: false };
    }
    /*
     * ⚠️ 15.09.2026 — HIER ENDETE DIE ANZEIGE BISHER MIT EINEM STRICH.
     *
     * Martin, mit Foto: Die Leiste stand auf „0:01 / –", waehrend ein
     * Abschnitt lief. Ohne Gesamtdauer gibt es auch kein Sprungziel — der
     * Regler ist dann tot (`seek.disabled = dur <= 0`).
     *
     * Die Engine muss dafuer niemanden fragen: Sie HAELT die Texte der
     * Stuecke, die noch kommen, und weiss, wie viel sie schon gespielt hat.
     * Das reicht fuer eine ehrliche Schaetzung — sie wird mit Tilde
     * angezeigt, verspricht also keine Genauigkeit.
     *
     * ⚠️ Nur ein RUECKFALL. Die Zeichenzahl des Aufrufers ist genauer (sie
     *    kennt den ganzen Abschnitt, nicht nur den Rest) und gilt zuerst.
     */
    const restZeichen =
      this.restStuecke.reduce((n, t) => n + t.length, 0) +
      this.vorrat.reduce((n, p) => n + p.text.length, 0);
    if (restZeichen > 0) {
      return {
        position,
        dauer: position + restZeichen / ZEICHEN_JE_SEKUNDE,
        geschaetzt: true,
      };
    }
    return { position, dauer: 0, geschaetzt: true };
  }

  /** Aktueller Zustand (für Aufrufer, die ihn direkt abfragen wollen). */
  getState(): NarrationState {
    return this.state;
  }

  /** True, solange die Engine arbeitet (URL beschaffen / Erst-Puffern). */
  isBusy(): boolean {
    return isBusyState(this.state);
  }

  private setState(next: NarrationState): void {
    if (this.state === next) return;
    this.state = next;
    /*
     * ⚠️ 07.09.2026 — DER Grund, warum der grosse Player nicht bedienbar war.
     *
     * Martin, mit Bild: *„Dieser Player hier ist eine Katastrophe. Abgesehen
     * davon, dass er nicht funktioniert — ich kann weder Pause noch Play
     * druecken."* Im Bild stand das Dreieck (Abspielen), waehrend die Uhr
     * bei 0:41 von 2:59 lief und er den Text hoerte.
     *
     * Die Engine hat sich beim Leitstand ANGEMELDET, ihm danach aber nie
     * gesagt, was sie tut. `klingt` blieb also auf dem Wert stehen, den
     * irgendwer zuletzt gesetzt hatte. Stand er auf false, war der Knopf ein
     * „Fortsetzen" fuer etwas, das schon lief — ein Druck darauf tat
     * sichtbar nichts, und „Anhalten" war gar nicht erreichbar.
     *
     * ⚠️ Nicht nur „playing" gilt als klingend. Zwischen zwei Stuecken geht
     *    der Zustand kurz auf „buffering"; wer daran den Knopf umdreht,
     *    laesst ihn mitten im Vorlesen flackern. Klingend heisst hier: es
     *    ist unterwegs und soll toenen.
     */
    this.platz?.melde(
      next !== "paused" && next !== "idle" && next !== "error",
    );
    this.onState(next);
  }

  private armStallTimer(): void {
    this.clearStallTimer();
    // "buffering" = es lief noch kein Ton -> Anlauf (Server synthetisiert).
    // Alles andere = die Wiedergabe stockt mittendrin -> echter Aussetzer.
    const budget =
      this.state === "buffering" ? FIRST_CHUNK_TIMEOUT_MS : STALL_TIMEOUT_MS;
    this.stallTimer = setTimeout(() => {
      this.stallTimer = null;
      // Bisher der einzige voellig stumme Fehlerweg — jetzt sichtbar.
      reportPlaybackIssue({
        stage: budget === FIRST_CHUNK_TIMEOUT_MS ? "first-chunk" : "stall",
        waitedMs: budget,
        state: this.state,
      });
      this.fail();
    }, budget);
  }

  private clearStallTimer(): void {
    if (this.stallTimer) {
      clearTimeout(this.stallTimer);
      this.stallTimer = null;
    }
  }

  /** Eigener Takt fuer die Anzeige — unabhaengig von `timeupdate`. */
  private starteTakt(): void {
    if (this.takt) return;
    this.zuletztUhr = { wann: Date.now(), zeit: this.audio.currentTime || 0 };
    this.takt = setInterval(() => this.tick(), 500);
  }

  private stoppeTakt(): void {
    if (this.takt) {
      clearInterval(this.takt);
      this.takt = null;
    }
    this.uhrGefrorenSeit = 0;
  }

  private tick(): void {
    if (this.state !== "playing" || this.audio.paused || this.audio.ended) return;
    const jetzt = Date.now();
    const zeit = this.audio.currentTime || 0;
    if (Math.abs(zeit - this.zuletztUhr.zeit) > 0.05) {
      // Das Element kommt mit — Waechter zuruecksetzen.
      this.zuletztUhr = { wann: jetzt, zeit };
      if (this.uhrGefrorenSeit > 0) this.uhrGefrorenSeit = 0;
    } else if (this.uhrGefrorenSeit === 0 && jetzt - this.zuletztUhr.wann >= 2000) {
      // Zwei Sekunden ohne Bewegung bei laufendem Ton: das ist der Fall.
      this.uhrGefrorenSeit = jetzt;
      if (!this.uhrGemeldet) {
        this.uhrGemeldet = true;
        reportPlaybackIssue({
          stage: "uhr-steht",
          currentTime: zeit,
          duration: this.audio.duration,
          readyState: this.audio.readyState,
          networkState: this.audio.networkState,
          paused: this.audio.paused,
          ended: this.audio.ended,
          gespielteSek: this.gespielteSek,
          einzeln: this.restStuecke.length === 0 && this.vorrat.length === 0,
          zeichen: this.stueckQuelle?.zeichen ?? this.quelleZeichen ?? 0,
          src: this.audio.currentSrc ? this.audio.currentSrc.slice(-60) : "",
          titel: this.letzterTitel,
        });
      }
    }
    const st = this.stand();
    this.onTime(st.position, st.dauer, st.geschaetzt);
  }

  /** Laufende Anfrage abbrechen (ohne Zustandswechsel). */
  private abortRequest(): void {
    if (this.reqAbort) {
      this.reqAbort.abort();
      this.reqAbort = null;
    }
  }

  /** Geht in den Fehlerzustand — erholbar: der nächste Klick startet neu. */
  private fail(): void {
    this.clearStallTimer();
    this.abortRequest();
    this.stoppeTakt();
    // 15.09.2026 (Pruefer-Befund): Nach dem Stall-Wachhund blieb das Element
    // auf paused=false, ended=false — es fiel durch jedes Netz, das den
    // Hi-Fi-Halt loest (pause/ended/Aufraeumer), und hielt das Mikrofon.
    try {
      if (!this.audio.paused) this.audio.pause();
    } catch {
      /* egal */
    }
    this.setState("error");
  }

  /**
   * Beschafft (über `source`) die URL und spielt ab. Bricht jede vorherige
   * Anfrage zuerst ab. Schon vorbereitete URLs kann der Aufrufer über
   * `preResolvedUrl` durchreichen (Prefetch) → Sofort-Start ohne `resolveUrl`.
   */
  /**
   * Holt das nächste Stück (meist schon vorbereitet) und spielt es.
   *
   * ⚠️ Das ÜBERNÄCHSTE wird sofort mitangefordert. Ohne diesen Vorlauf
   * entstünde zwischen zwei Stücken genau die Pause, die wir am Anfang
   * beseitigen wollten — nur eben mitten im Satz.
   */
  /**
   * Ein Stueck kam nicht — das darf nie lautlos passieren.
   *
   * Martin am 07.09.2026: *„Habe gerade meinen Spiegel vorlesen lassen, da
   * faellt ein ganzer Block. Der Teil mit dem Grab, mit dem Blut."* Auf dem
   * Server war kein Aufruf gescheitert; verloren ging es hier, und die
   * einzige Spur war — nichts.
   */
  private meldeAusfall(text: string): void {
    // 19.09.2026: Dieser Block stand WORTGLEICH auch in tts-queue.ts, und
    // beide meldeten JE ABSATZ — Martin bekam sechs Zettel uebereinander,
    // die den Text verdeckten. Jetzt eine Stelle, eine Meldung, mit Grund.
    meldeVorleseVerlust(text, "", "vorlesen");
  }

  /**
   * Den Vorrat wegwerfen.
   *
   * ⚠️ Die laufenden Anfragen laufen weiter — abbrechen koennen wir sie
   *    nicht, ohne dem Server die halbe Vertonung wegzureissen. Sie legen
   *    ihr Ergebnis in einen Posten, den niemand mehr liest, und der
   *    `loadSeq`-Vergleich haelt sie von allem Weiteren fern.
   */
  private leereVorrat(): void {
    for (const p of this.vorrat) {
      p.verworfen = true;
      if (p.fertig) this.gibFrei(p.erg?.url);
    }
    this.vorrat = [];
    this.offeneAnfragen = 0;
  }

  /**
   * Eine blob:-Adresse zurueckgeben. (10.09.2026)
   *
   * Die Stuecke kommen seit heute als blob: (wirklich geladen, nicht nur
   * angemeldet). Ein Spiegel sind rund dreissig davon, je ein paar hundert
   * Kilobyte — ohne Freigabe blieben sie bis zum Neuladen im Speicher des
   * Telefons. Freigegeben wird nur, was die Engine selbst bekommen hat;
   * eine vorgeholte Datei-Adresse des Aufrufers bleibt seine Sache.
   */
  private gibFrei(url: string | undefined): void {
    if (!url?.startsWith("blob:")) return;
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* kein URL-Objekt (Tests) */
    }
  }

  /** Den Einstieg in die Stuecke setzen — VOR dem ersten Ton. */
  private setzeEinstieg(source: AudioSource): void {
    const start = source.startSek ?? 0;
    const alle = source.stuecke ?? [];
    if (start <= 2 || alle.length === 0) return;
    const dauern = alle.map((t) => t.length / ZEICHEN_JE_SEKUNDE);
    const { index, davor } = stueckFuerPosition(dauern, start);
    this.restStuecke = alle.slice(index);
    this.gespielteSek = davor;
    this.zielOffset = start - davor;
  }

  /** Wie viel Vorsprung dieser Text braucht — in Sekunden Ton. */
  private zielVorratSek(): number {
    const zeichen = this.stueckQuelle?.zeichen ?? 0;
    const gesamt = zeichen / ZEICHEN_JE_SEKUNDE;
    return Math.min(
      VORLAUF_MAX_SEK,
      Math.max(VORLAUF_MIN_SEK, gesamt * VORLAUF_ANTEIL),
    );
  }

  /** Sekunden im Vorrat — angefordert, egal ob schon da. */
  private vorratSek(): number {
    return this.vorrat.reduce((a, p) => a + p.sek, 0);
  }

  /** Sekunden, die WIRKLICH abspielbar sind. */
  private bereitSek(): number {
    return this.vorrat.reduce((a, p) => a + (p.fertig && p.erg?.url ? p.sek : 0), 0);
  }

  /**
   * Nachschub anfordern, bis der Vorrat reicht.
   *
   * ⚠️ Ruft sich nach jeder fertigen Anfrage selbst wieder auf — so bleibt
   *    der Vorrat waehrend des Sprechens gefuellt, ohne einen Taktgeber.
   */
  private fuelleVorrat(seq: number): void {
    const quelle = this.stueckQuelle;
    if (!quelle?.resolveStueck) return;
    if (seq !== this.loadSeq) return;
    const ziel = this.zielVorratSek();
    /*
     * ⚠️ 10.09.2026 — im Netz-Log des Spiegels gemessen: das naechste Stueck
     *    wurde erst angefordert, als das vorige zu SPIELEN begann (der
     *    Vorrat mass Sekunden, und ein einziges Stueck von 25 s erfuellte
     *    das Ziel). Ein Stueck von 420 Zeichen braucht ~22 s Synthese, das
     *    davor spielt aber nur 20 s — eine Luecke von 2–3 s, mitten im Text.
     *    Darum haelt der Vorrat immer ZWEI Stuecke in Arbeit oder bereit:
     *    dann hat jedes Stueck ein ganzes Stueck Vorsprung.
     */
    while (
      this.restStuecke.length > 0 &&
      this.offeneAnfragen < STUECK_PARALLEL &&
      (this.vorratSek() < ziel || this.vorrat.length < 2)
    ) {
      const text = this.restStuecke.shift()!;
      const posten: StueckPosten = {
        text,
        sek: text.length / ZEICHEN_JE_SEKUNDE,
        fertig: false,
        erg: null,
        p: Promise.resolve({ url: "" }),
      };
      this.offeneAnfragen++;
      posten.p = quelle
        .resolveStueck(text, new AbortController().signal)
        .then((u): StueckErgebnis => ({ url: u || "" }))
        // Der Fehler wird GEMERKT, nicht in eine leere Adresse verwandelt —
        // leer heisst „nichts zu sprechen" und wird uebersprungen.
        .catch((fehler): StueckErgebnis => ({ url: "", fehler }))
        .then((e) => {
          posten.fertig = true;
          posten.erg = e;
          if (posten.verworfen) this.gibFrei(e.url);
          this.offeneAnfragen--;
          this.fuelleVorrat(seq);
          return e;
        });
      this.vorrat.push(posten);
    }
  }

  /**
   * Warten, bis genug Ton beisammen ist — nur vor dem ERSTEN Stueck.
   *
   * Danach traegt der Vorrat: solange er haelt, folgt Stueck auf Stueck ohne
   * Pause. Laeuft er trotzdem leer, wird gewartet (der Aufrufer `await`et das
   * naechste Versprechen) — nie uebersprungen.
   */
  private async warteAufVorsprung(seq: number): Promise<void> {
    const ziel = this.zielVorratSek();
    for (;;) {
      if (seq !== this.loadSeq) return;
      if (this.bereitSek() >= ziel) return;
      // Alles angefordert und alles da: mehr wird es nicht.
      if (this.restStuecke.length === 0 && this.offeneAnfragen === 0) return;
      const offen = this.vorrat.find((x) => !x.fertig);
      if (!offen) return;
      await offen.p;
    }
  }

  /**
   * Das naechste Stueck aus dem Vorrat spielen.
   *
   * ⚠️ Hier stand bis zum 07.09. ein Vorlauf auf GENAU EIN Stueck. Bei einer
   *    Erzeugung, die langsamer ist als die Wiedergabe, reicht das nie: nach
   *    jedem Stueck war das naechste noch nicht fertig, und es entstand die
   *    Pause, die Martin gehoert hat. Jetzt gibt es einen echten Vorrat.
   */
  private async spieleNaechstesStueck(): Promise<void> {
    const quelle = this.stueckQuelle;
    if (!quelle?.resolveStueck) {
      this.setState("idle");
      this.onEnded();
      return;
    }
    const seq = this.loadSeq;
    this.fuelleVorrat(seq);
    // Vor dem ersten Ton sammeln; danach ist der Vorrat schon da.
    if (this.gespielteSek === 0) {
      this.setState("buffering");
      await this.warteAufVorsprung(seq);
      if (seq !== this.loadSeq) return;
    }
    const posten = this.vorrat.shift();
    if (!posten) {
      this.stueckQuelle = null;
      this.setState("idle");
      this.onEnded();
      return;
    }
    let erg: StueckErgebnis;
    try {
      // Ist es noch unterwegs, wird GEWARTET — nicht uebersprungen.
      if (!posten.fertig) this.setState("buffering");
      erg = await posten.p;
    } catch (err) {
      if (seq !== this.loadSeq || isAbortError(err)) return;
      this.fail();
      return;
    }
    if (seq !== this.loadSeq) return;
    this.fuelleVorrat(seq);
    if (!erg.url) {
      if (erg.fehler !== undefined) {
        /*
         * ⚠️ Gescheitert, nicht leer. Ein zweiter Versuch JETZT: zwischen der
         *    Anfrage und diesem Moment ist ein ganzes Stueck gelaufen.
         */
        const spaet = await quelle
          .resolveStueck(posten.text, new AbortController().signal)
          .catch(() => "");
        if (seq !== this.loadSeq) return;
        if (spaet) {
          erg = { url: spaet };
        } else {
          this.meldeAusfall(posten.text);
        }
      }
      if (!erg.url) {
        // Leer heisst „nichts zu sprechen" (ein Stueck aus lauter
        // Auszeichnung, Ticket #2060) — weiter mit dem naechsten.
        if (this.vorrat.length > 0 || this.restStuecke.length > 0) {
          void this.spieleNaechstesStueck();
          return;
        }
        this.stueckQuelle = null;
        this.setState("idle");
        this.onEnded();
        return;
      }
    }
    this.setState("buffering");
    this.gibFrei(this.laufendeStueckUrl);
    this.laufendeStueckUrl = erg.url;
    this.audio.src = erg.url;
    void this.audio.play().catch((err) => {
      if (isAbortError(err)) return;
      this.fail();
    });
  }

  loadAndPlay(source: AudioSource, preResolvedUrl?: string | null): void {
    // Neuer Vorgang — die Melde-Bremse loest sich (siehe vorlese-verlust.ts).
    neuerVorlesevorgang();
    this.letzterTitel = source.titel ?? "";
    // 2026-07-15 (Martin): A2DP-Umschlag schon beim Abspiel-Wunsch anstossen,
    // damit der erste Ton hochwertig ist (Bluetooth-Handschlag laeuft parallel
    // zum Beschaffen der URL / zur Synthese).
    prewarmHiFi();
    this.abortRequest();
    this.clearStallTimer();
    const seq = ++this.loadSeq;
    // Ticket 2047: Bietet die Quelle Stücke an, laufen wir sie der Reihe nach
    // ab — der erste Ton kommt dann nach dem ersten Stück statt nach der
    // ganzen Antwort. Sonst bleibt alles beim Alten.
    this.gespielteSek = 0;
    /*
     * ⚠️⚠️ 15.09.2026 — DER FEHLENDE ANFANG BEIM SPURWECHSEL.
     *
     * Martin: *„Wenn er von einer Spur zur anderen wechselt, oftmals fehlt
     * der Anfang des ersten oder des naechsten Stuecks."*
     *
     * `zielOffset` ist der Einstiegs-Versatz (Wiedereinstieg oder Sprung mit
     * dem Regler). Zurueckgesetzt wurde er NUR, nachdem er angewandt wurde —
     * im `loadedmetadata`-Zuhoerer. Wurde er nie verbraucht (Sprung auf ein
     * Stueck, das wegen eines Cache-Fehltreffers 5 bis 14 Sekunden braucht,
     * und davor der Abschnittswechsel), UEBERLEBTE er den Lauf und wurde im
     * naechsten Abschnitt auf dessen ERSTES Stueck angewandt.
     *
     * Nachgestellt: Der neue Abschnitt begann bei Sekunde 4,06 statt 0. Ist
     * der Rest-Versatz groesser als das erste Stueck, greift die Klammer
     * `d - 0,25` — dann faellt das erste Stueck praktisch ganz aus.
     *
     * Ein Versatz gehoert zu EINEM Lauf. Ein neuer Lauf beginnt bei null.
     */
    this.zielOffset = 0;
    this.leereVorrat();
    this.gibFrei(this.laufendeStueckUrl);
    this.laufendeStueckUrl = "";
    this.quelleZeichen = source.zeichen ?? 0;
    this.gemesseneDauer = 0;
    this.zuletztGemessen = 0;
    this.istStrom = false;
    this.stoppeTakt();
    this.uhrGemeldet = false;
    /*
     * ⚠️ 06.09.2026 — der Spiegel um 09:03: Der Player hatte beim Oeffnen den
     * GANZEN Abschnitt vorgeholt (preResolvedUrl, Cache-Treffer), reichte beim
     * Play aber Quelle UND Stuecke herein — und dieser Zweig griff zuerst:
     * `pruefeGanz && !preResolvedUrl` war falsch, also Stueck 1 (90 Zeichen),
     * nie im Cache, kalt vertont. Eine fertige Datei gewinnt IMMER ueber die
     * Stueck-Logik; die Stuecke sind der Weg fuer den kalten Start.
     */
    if (source.stuecke?.length && source.resolveStueck && !preResolvedUrl) {
      /*
       * ⚠️ ERST fragen, ob der ganze Text schon da ist. (05.09.2026)
       *
       * Ist er es, spielen wir EINE Datei: Dauer bekannt, springbar, Leiste
       * exakt. Erst wenn nicht, kommen die Stücke — der Weg für den kalten
       * Start, nicht der Normalfall. Vorher wurde IMMER gestückelt, und die
       * nächtliche Vorwärmung des ganzen Abschnitts lag ungenutzt da.
       */
      if (source.pruefeGanz) {
        this.stueckQuelle = source;
        this.becomeActive(this.letzterTitel);
        this.setState("preparing");
        const controller = new AbortController();
        this.reqAbort = controller;
        void source
          .pruefeGanz(controller.signal)
          .catch(() => null)
          .then((url) => {
            if (seq !== this.loadSeq) return;
            if (this.reqAbort === controller) this.reqAbort = null;
            if (url) {
              // Als Einzeldatei weiter — die Stück-Logik bleibt aus.
              this.stueckQuelle = null;
              this.restStuecke = [];
              this.loadAndPlay({ ...source, stuecke: undefined }, url);
              return;
            }
            this.restStuecke = [...source.stuecke ?? []];
            this.setzeEinstieg(source);
            void this.spieleNaechstesStueck();
          });
        return;
      }
      this.stueckQuelle = source;
      this.restStuecke = [...source.stuecke];
      this.setzeEinstieg(source);
      this.becomeActive(this.letzterTitel);
      this.setState("preparing");
      void this.spieleNaechstesStueck();
      return;
    }
    this.stueckQuelle = null;
    this.restStuecke = [];

    const startPlayback = (url: string): void => {
      if (seq !== this.loadSeq) return; // veraltet — neuer Aufruf gewann
      this.becomeActive(this.letzterTitel); // #3: nur EIN Ton auf der Bühne
      this.setState("buffering");
      // Der gemerkte Stand — angewandt, sobald die Datei ihre Dauer kennt.
      const start = source.startSek ?? 0;
      if (start > 2) this.zielOffset = start;
      this.audio.src = url;
      // Pre.673-Muster: play() NICHT awaiten. Bei stockendem Stream bleibt das
      // play()-Promise pending; die Engine reagiert stattdessen auf die
      // Audio-Events („playing"/„waiting"/„error") + Watchdog. Wird die
      // Wiedergabe vom Browser blockiert/abgelehnt → Fehlerzustand.
      void this.audio.play().catch((err) => {
        if (isAbortError(err)) return; // Quellwechsel — kein echter Fehler
        // Der Name ist die Diagnose: "NotAllowedError" = der Browser hat den
        // automatischen Start ohne frische Nutzer-Geste verweigert (genau der
        // Verdacht beim Abschnitts-Uebergang im Spiegel).
        reportPlaybackIssue({
          stage: "play",
          name: err instanceof Error ? err.name : String(err),
          message: err instanceof Error ? err.message : "",
          preResolved: !!preResolvedUrl,
        });
        this.fail();
      });
    };

    if (preResolvedUrl) {
      startPlayback(preResolvedUrl);
      return;
    }

    this.setState("preparing");
    const controller = new AbortController();
    this.reqAbort = controller;
    withTimeout(
      source.resolveUrl(controller.signal),
      synthFrist(source.zeichen),
      controller,
    )
      .then((url) => {
        if (seq !== this.loadSeq) return; // veraltet
        if (this.reqAbort === controller) this.reqAbort = null;
        if (!url) {
          this.fail();
          return;
        }
        startPlayback(url);
      })
      .catch((err) => {
        if (seq !== this.loadSeq) return; // veraltet → Zustand nicht anfassen
        if (this.reqAbort === controller) this.reqAbort = null;
        if (isAbortError(err)) return; // bewusst abgebrochen → kein Fehler-UI
        reportPlaybackIssue({
          stage: "resolve",
          name: err instanceof Error ? err.name : String(err),
          message: err instanceof Error ? err.message : "",
        });
        this.fail();
      });
  }

  /** Play↔Pause umschalten. Im Fehlerzustand: nichts (Aufrufer startet neu). */
  togglePlayPause(): void {
    if (this.state === "playing" || this.state === "stalled") {
      this.audio.pause();
      return;
    }
    if (this.audio.src && (this.audio.paused || this.audio.ended)) {
      // Fortsetzen ohne neue Beschaffung.
      this.becomeActive(this.letzterTitel); // #3: nur EIN Ton auf der Bühne
      this.setState("buffering");
      void this.audio.play().catch((err) => {
        if (isAbortError(err)) return;
        this.fail();
      });
    }
  }

  /** Pausiert, falls gerade abgespielt wird. */
  pause(): void {
    if (!this.audio.paused) this.audio.pause();
  }

  /**
   * Bühne beanspruchen.
   *
   * 2026-09-04: Vorher pausierte das hier nur die vorherige NarrationEngine
   * (`activeNarrationEngine`). Ein zweiter Ring derselben Art lag in
   * audio-player.ts — und die beiden wussten nichts voneinander. Genau daher
   * Martins Beobachtung: *„Man hört zwar eine Stimme, aber der Player läuft
   * offensichtlich nicht."*
   *
   * Jetzt melden sich beide beim selben Leitstand an. Wer die Bühne bekommt,
   * verdrängt den anderen — quer über alle Wege.
   */
  private becomeActive(titel?: string): void {
    if (activeNarrationEngine && activeNarrationEngine !== this) {
      activeNarrationEngine.pause();
    }
    // eslint-disable-next-line @typescript-eslint/no-this-alias -- die EINE aktive Engine, kein Alias fuer Rueckrufe
    activeNarrationEngine = this;
    this.platz = betritt({
      art: "vorlesen",
      titel: titel?.trim() || "Vorlesen",
      anhalten: () => this.pause(),
      // Stopp heisst: Wiedergabe aus, Warteschlange leer, Buehne frei.
      // `destroy()` raeumt beides und ruft selbst `platz.verlasse()`.
      beenden: () => this.destroy(),
      fortsetzen: () => void this.audio.play().catch(() => {}),
      // Dieselbe Rechnung wie für die Leiste (`stand()`) — zwei Rechnungen
      // waren der Grund, warum Leiste und Leitstand verschieden zählten.
      fortschritt: () => {
        const st = this.stand();
        return { position: st.position, dauer: st.dauer, geschaetzt: st.geschaetzt };
      },
      // Rohe Abspielzeit ohne Wanduhr-Zuschlag: so erkennt lange-spur.ts eine wirklich stehende Wiedergabe.
      abspielzeit: () => this.gespielteSek + (this.audio.currentTime || 0),
      // Die Tonleiste springt relativ (±15 s) — auch über Stückgrenzen.
      springe: (sek) => this.springeZu(this.stand().position + sek),
    });
  }

  /**
   * Setzt die Engine vollständig zurück: laufende Anfrage abbrechen, Audio
   * stoppen, Quelle leeren, Zustand → idle. Nach `destroy()` ist die Engine
   * wieder verwendbar (kein endgültiges Wegwerfen).
   */
  destroy(): void {
    this.abortRequest();
    this.clearStallTimer();
    // ⚠️ Auch hier: ein unverbrauchter Versatz darf den naechsten Lauf nicht
    //    erben (siehe loadAndPlay).
    this.zielOffset = 0;
    this.gemesseneDauer = 0;
    this.zuletztGemessen = 0;
    this.istStrom = false;
    this.stoppeTakt();
    this.loadSeq++; // alle schwebenden Auflösungen entwerten
    if (!this.audio.paused) this.audio.pause();
    this.audio.removeAttribute("src");
    this.gibFrei(this.laufendeStueckUrl);
    this.laufendeStueckUrl = "";
    try {
      this.audio.load(); // Buffer/Netzwerk wirklich freigeben
    } catch {
      /* einige Browser werfen ohne src — egal */
    }
    this.setState("idle");
    // Audit-Fix 2026-06-28: globale aktive-Engine-Referenz freigeben,
    // sonst haengt die zerstoerte Engine im Speicher (Memory-Leak).
    if (activeNarrationEngine === this) activeNarrationEngine = null;
    this.platz?.verlasse();
    this.platz = null;
    // Ticket 2047: Auch die Warteschlange räumen. Bliebe sie stehen, spielte
    // die nächste Wiedergabe die Reste der alten weiter.
    this.restStuecke = [];
    this.stueckQuelle = null;
    this.leereVorrat();
    this.gespielteSek = 0;
  }
}
